import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useReducer,
} from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { OpenParams, Panel, PreviewPanelsAction, PreviewPanelsApiState } from '../types';
import { usePanelWidth } from '../use-panel-width';

import { doPreviewPanelTypesMatch } from './utils';

const noop = () => {};

const initialState: PreviewPanelsApiState = {
	// Configured when opening a panel.
	panel: null,
	onOpen: noop,
	onClose: noop,
};

export const PreviewPanelsContext: React.Context<
	| {
			close: (onClose?: () => void) => void;
			open: (params: OpenParams) => void;
			setPanelWidth: (width: number) => void;
			state: PreviewPanelsApiState;
			width: number;
	  }
	| undefined
> = createContext<
	| {
			close: (onClose?: () => void) => void;
			open: (params: OpenParams) => void;
			setPanelWidth: (width: number) => void;
			state: PreviewPanelsApiState;
			width: number;
	  }
	| undefined
>(undefined);

const previewPanelsReducer = (
	state: PreviewPanelsApiState,
	action: PreviewPanelsAction,
): PreviewPanelsApiState => {
	switch (action.type) {
		case 'CLOSE':
			// Action explicitly called in order to close the active panel.
			action.onClose?.();
			state.panel && state?.onPreviewPanelClose?.(state.panel);
			return {
				...state,
				panel: null,
				onOpen: noop,
				onClose: noop,
			};
		case 'OPEN':
			// Action explicitly called in order to open a new panel.
			const { params } = action;

			// If the panel is already open, do nothing.
			if (state.panel?.id === params.panel.id) {
				return state;
			}

			/*
				When opening a new preview panel, if the current panel is of the same type we don't
				want to close the panel, but rather swap out the content. If the panel types don't match
				we close the current panel and open the new one.
				ex: A Goals panel is already open, and user tries to open a different Goals panel.
				Parent Products must pass their preview panel close functions to the Preview Panel provider
				via the `onPreviewPanelClose` prop.
			*/
			if (state.panel && !doPreviewPanelTypesMatch(state.panel, params.panel)) {
				// Close the active panel.
				state.onPreviewPanelClose?.(state.panel);
			}
			state.panel && state.onClose();

			// Open the new panel.
			params.onOpen?.();
			state?.onPreviewPanelOpen?.(params.panel);
			return {
				...state,
				panel: params.panel,
				onOpen: params.onOpen || initialState.onOpen,
				onClose: params.onClose || initialState.onClose,
			};
		default:
			return state;
	}
};

export const PreviewPanelsProvider = ({
	onPreviewPanelOpen,
	onPreviewPanelClose,
	parentProduct,
	children,
	initialPanel,
	initialWidth,
}: {
	children: ReactNode;
	initialPanel?: Panel;
	/**
	 * Optional initial panel width. When provided (e.g. by reading the
	 * persisted width from a server-prefetched store), the panel renders at
	 * this width on the first paint, avoiding the visible re-layout that
	 * normally occurs when the persisted width arrives after hydration.
	 *
	 * When omitted, the panel uses the viewport-based default
	 */
	initialWidth?: number;
	onPreviewPanelClose?: (panel: Panel) => void;
	onPreviewPanelOpen?: (panel: Panel) => void;
	parentProduct: string;
}): React.JSX.Element => {
	const { panelWidth, onPanelClosed, onPanelOpened, onPanelWidthChanged } = usePanelWidth(
		fg('nin_glance_panel_initial_width_ssr') ? { initialWidth } : undefined,
	);

	const [state, dispatch] = useReducer(previewPanelsReducer, {
		...initialState,
		...(initialPanel && fg('preview_panel_initial_state') ? { panel: initialPanel } : {}),
		onPreviewPanelOpen,
		onPreviewPanelClose,
	});

	const setPanelWidth = useCallback(
		(width: number) => {
			onPanelWidthChanged(width, state.panel?.product, state.panel?.type, parentProduct);
		},
		[onPanelWidthChanged, parentProduct, state],
	);

	const close = useCallback(
		(onClose?: () => void) => {
			dispatch?.({ type: 'CLOSE', onClose });
			onPanelClosed();
		},
		[dispatch, onPanelClosed],
	);

	const open = useCallback(
		(params: OpenParams) => {
			dispatch?.({ type: 'OPEN', params });
			onPanelOpened(params.width);
		},
		[dispatch, onPanelOpened],
	);

	const context = useMemo(
		() => ({
			state,
			open,
			close,
			width: panelWidth,
			setPanelWidth,
		}),
		[close, panelWidth, open, setPanelWidth, state],
	);

	return <PreviewPanelsContext.Provider value={context}>{children}</PreviewPanelsContext.Provider>;
};

// Hook to use the context
export const usePreviewPanelsContext = ():
	| {
			close: (onClose?: () => void) => void;
			open: (params: OpenParams) => void;
			setPanelWidth: (width: number) => void;
			state: PreviewPanelsApiState;
			width: number;
	  }
	| undefined => {
	return useContext(PreviewPanelsContext);
};
